import { usePromosPlacementEngine } from "modules/promos/hooks/usePromosPlacementEngine"
import { useEffect } from "react"
import { Creative } from "modules/promos/components/Slot/Creative"
import { useHasBeenInViewport } from "common/hooks/interaction/useHasBeenInViewport"
import type { PromoDataProps } from "modules/promos/types"
import { trackPromoEvent, PromoTrackingEvent } from "modules/promos/lib/tracking"
import { setUTMParams } from "common/lib/setUtmParams"
import { useRegionConfig } from "modules/i18n/hooks/useRegionConfig"
import { trackPromoEngaged, trackPromoViewed } from "common/lib/tcEventCollector"

interface RendererProps {
  promo: PromoDataProps
}

/**
 * Component that will render a themed promo creative, attaching placement engine events to it.
 */
export const Renderer = ({ promo }: RendererProps) => {
  const {
    events: { onClose, onView, onClick },
  } = usePromosPlacementEngine()
  const { setRef: creativeRef, hasBeenInViewport } = useHasBeenInViewport(100)
  const regionConfig = useRegionConfig()

  useEffect(() => {
    if (hasBeenInViewport) {
      trackPromoEvent(promo, PromoTrackingEvent.Impression)
      trackPromoViewed(promo.promoId.toString(), promo.type)

      onView(promo)
    }
  }, [promo, onView, hasBeenInViewport, regionConfig])

  const events = {
    onClick: () => {
      trackPromoEvent(promo, PromoTrackingEvent.Engagement)
      trackPromoEngaged(promo.promoId.toString(), promo.type)
      onClick(promo)

      if (promo.data?.href) {
        const hrefWithUTMParams = setUTMParams(promo.data.href, {
          campaign: promo.campaign?.name,
          medium: promo.type,
          content: `promo-${promo.promoId}`,
        })

        // When changing the browser location, the browser kills off outstanding javascript
        // coroutines, including attempts to send tracking information.
        // If a very slight delay is introduced, it gives those requests a chance to be enqueued,
        // and then other mechanisms are in place to try and get the browser to deliver them.
        setTimeout(() => {
          window.location.assign(hrefWithUTMParams)
        }, 5)
      }
    },
    onClose: () => {
      trackPromoEvent(promo, PromoTrackingEvent.Dismiss)
      onClose(promo)
    },
  }

  const creativeProps = { promo, events, creativeRef }

  return <Creative {...creativeProps} />
}
